import React from "react";
import {
    ComparePricesButton,
    ShippingInfo,
    ViewSellerDetails,
} from "web/react/components/buy-option-card";
import { Promotion } from "web/react/components/promotion/promotion";
import { Button } from "web/react/emo/button";
import { HStack } from "web/react/emo/h-stack";
import { Price } from "web/react/emo/price";
import { RadioCardGroup } from "web/react/emo/radio-card-group";
import { RadioButtonWithLabelProps } from "web/react/emo/radio-card-group/radio-button-with-label";
import { RetailerWithLogo } from "web/react/emo/retailer-with-logo";
import { Sticker, StickerType } from "web/react/emo/sticker";
import { VStack } from "web/react/emo/v-stack";
import { BuyButton } from "web/react/pages/product/in-stock/in-stock-product-page/buy-button/buy-button";
import {
    BuyOptionPromotionSerializer,
    ShippingInformation,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { ProductBuyOptionsSerializer } from "web/types/serializers";
import * as styles from "./buy-option-card.css";

interface BuyOptionCardBaseProps {
    buyOption: ProductBuyOptionsSerializer;
    buyOptionIndex: number;
    retailerLogoURL?: string;
    stickerType: StickerType | null;
    shippingInformation: ShippingInformation[];
    promotion: BuyOptionPromotionSerializer | null;
    comparePricesCount: number;
    onBuyButtonClick?: () => void;
    onClickCompare?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    onShowCompare?: () => void;
    onRetailerClick?: () => void;
    isCheckout?: boolean;
}

interface CardProps extends BuyOptionCardBaseProps {
    variant: "narrow" | "wide";
}

interface RadioCardProps extends BuyOptionCardBaseProps, RadioButtonWithLabelProps {
    variant: "radio";
}

export type BuyOptionCardProps = CardProps | RadioCardProps;

export function BuyOptionCard(props: BuyOptionCardProps): React.ReactElement | null {
    const {
        variant,
        buyOption,
        buyOptionIndex,
        retailerLogoURL,
        stickerType = null,
        shippingInformation,
        comparePricesCount,
        promotion = null,
        onBuyButtonClick,
        onClickCompare,
        onShowCompare,
        onRetailerClick,
        isCheckout = false,
    } = props;

    const isDeepAffiliatePhase1Enabled = environment.getFeature(
        "deep_affiliate_experiment_phase_1"
    );
    const isCheckoutBuyOption = !!buyOption.is_icon && isCheckout;
    const isVerifiedRetailer = isCheckoutBuyOption && !isDeepAffiliatePhase1Enabled;

    if (variant === "narrow") {
        return (
            <div className={styles.narrowCard} data-testid="buy-option-card-narrow">
                <div className={styles.row}>
                    <Price
                        fullPrice={buyOption.price}
                        inStock={Boolean(buyOption.in_stock)}
                        salePrice={buyOption.sale_price}
                    />

                    <a
                        href={buyOption.retailer_link}
                        data-testid="retailer-internal-link"
                        onClick={onRetailerClick}
                    >
                        <div className={styles.retailerWithLogo}>
                            <RetailerWithLogo
                                retailerName={buyOption.retailer_name}
                                retailerLogoURL={retailerLogoURL}
                                isVerified={isVerifiedRetailer}
                            />
                        </div>
                    </a>
                </div>
                {!!stickerType && <Sticker name={stickerType} />}
                <ShippingInfo
                    buyOptionId={buyOption.link_id}
                    shippingInformation={shippingInformation}
                    direction={"column"}
                />
                {promotion && (
                    <div className={styles.narrowCardPromo}>
                        <Promotion
                            pageType="pdp"
                            message={promotion.message}
                            promoCode={promotion.promoCode}
                            isActive={true}
                        />
                    </div>
                )}
                <HStack spacing="xxs">
                    <div className={styles.fullWidth}>
                        <BuyButton
                            variant="affiliate"
                            buyOption={buyOption}
                            buyOptionPosition={buyOptionIndex}
                            sendExtraAnalytics={false}
                            isComparison={true}
                            CustomButtonComponent={
                                <Button
                                    variant="primary"
                                    width={"full"}
                                    title={gettext("product.buy_from_store")}
                                    icon="external-link"
                                    iconPosition="right"
                                    onClick={onBuyButtonClick}
                                />
                            }
                        />
                    </div>
                    {comparePricesCount > 1 && (
                        <ComparePricesButton
                            onClick={onClickCompare}
                            onShow={onShowCompare}
                            count={comparePricesCount}
                        />
                    )}
                </HStack>
                {isCheckout && <ViewSellerDetails />}
            </div>
        );
    }

    if (variant === "wide") {
        return (
            <div className={styles.wideCard} data-testid="buy-option-card-wide">
                <div className={styles.wideCardInner}>
                    <VStack spacing="xs">
                        <HStack spacing="xs">
                            <Price
                                fullPrice={buyOption.price}
                                inStock={Boolean(buyOption.in_stock)}
                                salePrice={buyOption.sale_price}
                            />
                            <a
                                href={buyOption.retailer_link}
                                data-testid="retailer-internal-link"
                                onClick={onRetailerClick}
                            >
                                <RetailerWithLogo
                                    retailerName={buyOption.retailer_name}
                                    retailerLogoURL={retailerLogoURL}
                                    isVerified={isVerifiedRetailer}
                                />
                            </a>
                        </HStack>
                        {!!stickerType && <Sticker name={stickerType} />}
                        <ShippingInfo
                            buyOptionId={buyOption.link_id}
                            shippingInformation={shippingInformation}
                            direction={"row"}
                        />
                    </VStack>
                    <VStack spacing="xxs">
                        <BuyButton
                            variant="affiliate"
                            buyOption={buyOption}
                            buyOptionPosition={buyOptionIndex}
                            sendExtraAnalytics={false}
                            isComparison={true}
                            CustomButtonComponent={
                                <Button
                                    variant="primary"
                                    width={"fit-content"}
                                    title={gettext("product.buy_from_store")}
                                    icon="external-link"
                                    iconPosition="right"
                                    onClick={onBuyButtonClick}
                                />
                            }
                        />
                        {isCheckout && <ViewSellerDetails />}
                    </VStack>
                </div>
                {promotion && (
                    <div className={styles.wideCardPromo}>
                        <Promotion
                            pageType="pdp"
                            message={promotion.message}
                            promoCode={promotion.promoCode}
                            isActive={true}
                        />
                    </div>
                )}
            </div>
        );
    }

    if (variant === "radio") {
        const { id, name, value, checked, onChange } = props;

        return (
            <RadioCardGroup.Card
                banner={
                    promotion ? (
                        <Promotion
                            pageType="pdp"
                            message={promotion.message}
                            promoCode={promotion.promoCode}
                            isActive={true}
                        />
                    ) : null
                }
                selected={checked}
                onClick={onChange}
            >
                <div className={styles.radioCard} data-testid="buy-option-card-radio">
                    <div className={styles.row}>
                        <RadioCardGroup.Button
                            id={id}
                            name={name}
                            value={value}
                            checked={checked}
                            onChange={onChange}
                        >
                            <Price
                                fullPrice={buyOption.price}
                                inStock={Boolean(buyOption.in_stock)}
                                salePrice={buyOption.sale_price}
                            />
                        </RadioCardGroup.Button>
                        <div className={styles.retailerWithLogo}>
                            <RetailerWithLogo
                                retailerName={buyOption.retailer_name}
                                retailerLogoURL={retailerLogoURL}
                                isVerified={isVerifiedRetailer}
                            />
                        </div>
                    </div>
                    {!!stickerType && (
                        <div className={styles.sticker}>
                            <Sticker name={stickerType} />
                        </div>
                    )}
                    <ShippingInfo
                        buyOptionId={buyOption.link_id}
                        shippingInformation={shippingInformation}
                        direction={"column"}
                    />
                </div>
            </RadioCardGroup.Card>
        );
    }

    return null;
}
